import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon } from '../icons'
import { CATEGORY_COLORS } from '../../utils/categoryColors'
import './CategoryColorPicker.css'

/**
 * Kategori rengini seçen yuvarlak düğme ve 12 renklik palet (22 Eki 2026).
 * Palet MoreMenu gibi body'ye taşınıp ekrana göre konumlanıyor; dışarı
 * basınca, Esc ile ya da kaydırınca kapanıyor.
 */
export function CategoryColorPicker({
  hex,
  chosen,
  categoryName,
  onPick,
}: {
  /** Şu an görünen renk (seçilmiş ya da varsayılan). */
  hex: string
  /** Patronun seçtiği renk; boşsa varsayılan kullanılıyor. */
  chosen?: string | null
  categoryName: string
  /** null: otomatik (sıraya göre varsayılan). */
  onPick: (id: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<CSSProperties>({ visibility: 'hidden' })
  const btn = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !btn.current || !pop.current) return
    const b = btn.current.getBoundingClientRect()
    const w = pop.current.offsetWidth
    const h = pop.current.offsetHeight
    const pad = 8
    const left = Math.min(Math.max(b.left, pad), window.innerWidth - w - pad)
    const below = b.bottom + 6
    const top = below + h > window.innerHeight - pad ? Math.max(pad, b.top - 6 - h) : below
    setPos({ left, top })
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    const onMove = () => setOpen(false)
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open])

  const pick = (id: string | null) => {
    setOpen(false)
    if (id !== (chosen ?? null)) onPick(id)
  }

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`ccolor ${open ? 'is-open' : ''}`}
        style={{ '--cat': hex } as CSSProperties}
        onClick={() => {
          setPos({ visibility: 'hidden' })
          setOpen((o) => !o)
        }}
        title="Kategorinin rengi"
        aria-label={`${categoryName} rengini değiştir`}
        aria-haspopup="dialog"
        aria-expanded={open}
      />
      {open &&
        createPortal(
          <div ref={pop} className="ccolor__pop" role="dialog" aria-label={`${categoryName} rengi`} style={pos}>
            <p className="ccolor__title">Kategori rengi</p>
            <div className="ccolor__grid">
              {CATEGORY_COLORS.map((c) => {
                const on = chosen ? chosen === c.id : false
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`ccolor__sw ${on ? 'is-on' : ''}`}
                    style={{ '--cat': c.hex } as CSSProperties}
                    onClick={() => pick(c.id)}
                    title={c.name}
                    aria-label={c.name}
                    aria-pressed={on}
                  >
                    {on && <CheckIcon />}
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              className={`ccolor__auto ${chosen ? '' : 'is-on'}`}
              onClick={() => pick(null)}
            >
              {!chosen && <CheckIcon />}
              Otomatik (sıraya göre)
            </button>
          </div>,
          document.body,
        )}
    </>
  )
}
